import React, { useMemo, useState } from "react";
import { drupalSettings } from "../../../utils/config";
import { editRecipientApi } from "../../../api/api";
import { useLoader } from "../../Common/GeneralLoader/GeneralLoaderProvider";

function initialsFromName(name) {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function iconForBankKey(key) {
  if (key === "account_number" || key === "iban") return "account";
  if (key.startsWith("routing_")) return "routing";
  if (key === "bank_country_code") return "country";
  if (key === "swift_code" || key.includes("routing")) return "routing";
  return "default";
}

const SummaryIcons = {
  account: (
    <svg className="edit-beneficiary-summary__icon-svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M4 10h16v10H4V10zm2 2v6h12v-6H6zm2 2h8v2H8v-2zM6 4h12v4H6V4zm2 2v1h8V6H8z" fill="currentColor" opacity=".85"/>
      <path d="M3 8h18v2H3V8z" fill="currentColor" opacity=".55"/>
    </svg>
  ),
  routing: (
    <svg className="edit-beneficiary-summary__icon-svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M4 12h16M8 8l-4 4 4 4M16 8l4 4-4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  ),
  country: (
    <svg className="edit-beneficiary-summary__icon-svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" fill="none"/>
      <path d="M3 12h18M12 3c2 4 2 14 0 18M12 3c-2 4-2 14 0 18" stroke="currentColor" strokeWidth="1.25" opacity=".6" fill="none"/>
    </svg>
  ),
  pin: (
    <svg className="edit-beneficiary-summary__icon-svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M12 22s8-4.5 8-11a8 8 0 10-16 0c0 6.5 8 11 8 11z" stroke="currentColor" strokeWidth="1.75" fill="none"/>
      <circle cx="12" cy="11" r="2.5" fill="currentColor"/>
    </svg>
  ),
  default: (
    <svg className="edit-beneficiary-summary__icon-svg" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      <path d="M12 8v5l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    </svg>
  ),
};

function buildDisplayRows(beneficiaryFormData) {
  const bd = beneficiaryFormData?.beneficiary?.bank_details || {};
  const rows = [];

  Object.entries(bd).forEach(([key, value]) => {
    if (key === "account_currency") {
      return;
    }
    if (key.startsWith("account_routing_type")) {
      const index = key.replace("account_routing_type", "");
      const typeLabel = String(value)
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      const typeValue = bd[`account_routing_value${index}`];
      const label = typeLabel === "Aba" ? "Account routing number" : typeLabel;
      rows.push({
        key,
        label: `${label}:`,
        value: typeValue,
        icon: "routing",
        sort: 20 + Number(index || 0) * 0.1,
      });
      return;
    }
    if (key.startsWith("account_routing_value")) return;

    const label = key === "iban"
      ? "IBAN"
      : key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    let sort = 50;
    if (key === "account_number" || key === "iban") sort = 10;
    else if (key === "bank_country_code") sort = 30;
    else if (key === "swift_code") sort = 35;
    else if (key === "account_currency") sort = 5;

    rows.push({
      key,
      label: `${label}:`,
      value,
      icon: iconForBankKey(key),
      sort,
    });
  });

  rows.sort((a, b) => a.sort - b.sort);

  const address = beneficiaryFormData?.beneficiary?.address;
  const addressStr = address
    ? Object.values(address).reverse().join(", ")
    : "—";

  rows.push({
    key: "address",
    label: "Address:",
    value: addressStr,
    icon: "pin",
    sort: 100,
  });

  return rows;
}

export const EditBeneficiaryConfirmStep = ({ payload, setPayload, setStep }) => {
  const [error, setError] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const formErrors = {
    technical_error: Drupal.t(
      "Sorry, there was an error. Please try again later or reach out to support@nautzero.com.",
    ),
  };

  const summary = useMemo(() => {
    const bf = payload?.beneficiaryFormData;
    const bd = bf?.beneficiary?.bank_details || {};
    const entityType = bf?.beneficiary?.entity_type || "";
    const currency = bd.account_currency || "";
    const paymentMethods = Array.isArray(bf?.payment_methods) ? bf.payment_methods : [];
    const methodLabels = paymentMethods.map((m) => {
      if (m === "LOCAL") return "ACH";
      if (m === "SWIFT") return "SWIFT";
      return m;
    });
    const title = bd.bank_name || bd.account_name || payload?.recipientName || Drupal.t("Recipient");
    const badgeParts = [entityType, currency, methodLabels.join(" · ")].filter(Boolean);
    const badge = badgeParts.join(" · ");

    return {
      title,
      initials: initialsFromName(title),
      badge,
      rows: buildDisplayRows(bf || {}),
    };
  }, [payload?.beneficiaryFormData, payload?.recipientName]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setError(false);
    showLoader();

    const requestPayload = {
      beneficiary_id: payload?.beneficiaryId,
      payload: { ...payload?.beneficiaryFormData },
      recipient_name:
        payload?.beneficiaryFormData?.beneficiary?.bank_details?.account_name || payload?.recipientName,
      uid: drupalSettings?.user?.user_id || drupalSettings?.user?.uid,
    };

    (async () => {
      try {
        const response = await editRecipientApi(requestPayload);
        if (!response?.result && response?.message?.code === 403) {
          const scaSessionCode =
            response?.message?.sca_session_code ??
            response?.message?.session_code ??
            response?.sca_session_code;
          if (scaSessionCode) {
            setPayload((prev) => ({
              ...prev,
              scaSessionCode,
              _editRecipientRequest: requestPayload,
            }));
            setStep(2.1);
            return;
          }
        }
        if (!response?.result) {
          setError(formErrors.technical_error);
          return;
        }
        jQuery(".view-bank-recipient").trigger("RefreshView");
        setStep(0);
      } catch (err) {
        const scaSessionCode =
          err?.response?.headers?.["x-sca-session-code"] ??
          err?.response?.data?.message?.sca_session_code ??
          err?.response?.data?.message?.session_code ??
          err?.response?.data?.sca_session_code;
        if (err?.response?.status === 403 && scaSessionCode) {
          setPayload((prev) => ({
            ...prev,
            scaSessionCode,
            _editRecipientRequest: requestPayload,
          }));
          setStep(2.1);
          return;
        }
        setError(formErrors.technical_error);
      } finally {
        hideLoader();
      }
    })();
  };

  return (
    <div className="edit-beneficiary-summary">
      <header className="edit-beneficiary-summary__header">
        <p className="edit-beneficiary-summary__eyebrow">{Drupal.t("Review before sending")}</p>
        <h2 className="edit-beneficiary-summary__title">{Drupal.t("Recipient summary")}</h2>
      </header>

      <div className="edit-beneficiary-summary__card">
        <div className="edit-beneficiary-summary__avatar" aria-hidden>
          {summary.initials}
        </div>
        <div className="edit-beneficiary-summary__card-text">
          <div className="edit-beneficiary-summary__name">{summary.title}</div>
          {summary.badge ? (
            <span className="edit-beneficiary-summary__badge">{summary.badge}</span>
          ) : null}
        </div>
      </div>

      <div className="add-recipient-body-content edit-beneficiary-summary__body">
        <form className="edit-beneficiary-summary__form" onSubmit={handleSubmit}>
          <ul className="edit-beneficiary-summary__rows">
            {summary.rows.map((row) => (
              <li key={row.key} className="edit-beneficiary-summary__row">
                <span className="edit-beneficiary-summary__row-icon" aria-hidden>
                  {SummaryIcons[row.icon] || SummaryIcons.default}
                </span>
                <span className="edit-beneficiary-summary__row-label">{row.label.replace(/:$/, "")}</span>
                <span className="edit-beneficiary-summary__row-value">{row.value}</span>
              </li>
            ))}
          </ul>

          {error ? (
            <div className="edit-beneficiary-summary__error popup-has-error">
              <div className="error_messages">{error}</div>
            </div>
          ) : null}

          <div className="edit-beneficiary-summary__footer">
            <button
              type="button"
              className="edit-beneficiary-summary__btn edit-beneficiary-summary__btn--ghost"
              onClick={() => setStep(1)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M4 20h4l12.5-12.5a2.1 2.1 0 000-3L17 3.5a2.1 2.1 0 00-3 0L4 14v6zM13.5 5.5l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {Drupal.t("Edit")}
            </button>
            <button type="submit" className="edit-beneficiary-summary__btn edit-beneficiary-summary__btn--primary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {Drupal.t("Confirm recipient")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
